/**
 * Structured Data (Schema.org) Generators
 *
 * This file contains functions to generate JSON-LD structured data
 * for improved search engine understanding and rich snippets.
 *
 * Usage:
 * ```typescript
 * import { generateOrganizationSchema } from '@/utils/structuredData';
 *
 * <Helmet>
 *   <script type="application/ld+json">
 *     {JSON.stringify(generateOrganizationSchema())}
 *   </script>
 * </Helmet>
 * ```
 */

interface FAQItem {
  question: string;
  answer: string;
}

interface Service {
  name: string;
  description: string;
  category?: string;
}

interface BreadcrumbItem {
  name: string;
  url: string;
}

/**
 * Organization Schema - Use on homepage and about page
 * Defines the company's basic information for search engines
 */
export const generateOrganizationSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "AI Genius Automations",
    alternateName: "AIG Consulting",
    url: "https://aigeniusautomations.com",
    logo: "https://aigeniusautomations.com/aig_consulting_logos_v4_darkLogoFull.png",
    description:
      "Custom AI-powered automation solutions for businesses. We eliminate operational friction and scale revenue through intelligent process automation with milestone-based pricing.",
    foundingDate: "2024",
    email: "noah@aigeniusautomations.com",
    sameAs: [
      // TODO: Add social media URLs when available
      // "https://linkedin.com/company/ai-genius-automations",
      // "https://twitter.com/aigenius"
    ],
    address: {
      "@type": "PostalAddress",
      addressLocality: "United States", // TODO: Update with specific city
      addressRegion: "US",
      addressCountry: "US",
    },
    contactPoint: {
      "@type": "ContactPoint",
      email: "noah@aigeniusautomations.com",
      contactType: "Customer Service",
      availableLanguage: "English",
    },
  };
};

/**
 * LocalBusiness Schema - Use on homepage and contact sections
 * Provides detailed business information for local search
 *
 * TODO: Add actual business address, phone number, and geo coordinates
 * when company information is finalized
 */
export const generateLocalBusinessSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: "AI Genius Automations",
    "@id": "https://aigeniusautomations.com",
    url: "https://aigeniusautomations.com",
    priceRange: "$$$$",
    image:
      "https://aigeniusautomations.com/aig_consulting_logos_v4_darkLogoFull.png",
    email: "noah@aigeniusautomations.com",
    // TODO: Uncomment and fill when business details available
    // "telephone": "+1-XXX-XXX-XXXX",
    // "address": {
    //   "@type": "PostalAddress",
    //   "addressLocality": "[City]",
    //   "addressRegion": "[State]",
    //   "postalCode": "[ZIP]",
    //   "addressCountry": "US"
    // },
    // "geo": {
    //   "@type": "GeoCoordinates",
    //   "latitude": "XX.XXXX",
    //   "longitude": "-XX.XXXX"
    // },
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00",
      closes: "17:00",
    },
  };
};

/**
 * Service Schema - Use for individual service offerings
 * Defines specific services for better search categorization
 */
export const generateServiceSchema = (service: Service) => {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: service.name,
    provider: {
      "@type": "Organization",
      name: "AI Genius Automations",
      url: "https://aigeniusautomations.com",
    },
    description: service.description,
    areaServed: "United States",
    category: service.category || "Business Automation",
  };
};

/**
 * Multiple Services Schema - Use on homepage or services page
 * Lists all primary service offerings
 */
export const generateServicesListSchema = () => {
  const services = [
    {
      name: "ERP System Integration",
      description:
        "Custom ERP integration services connecting fragmented business systems. Eliminate manual data entry and operational friction through intelligent automation.",
      category: "System Integration",
    },
    {
      name: "Workflow Automation",
      description:
        "AI-powered workflow automation to eliminate repetitive tasks, reduce errors, and scale operations without adding headcount.",
      category: "Process Automation",
    },
    {
      name: "Data Analytics & Reporting",
      description:
        "Real-time business intelligence and automated reporting systems that turn fragmented data into actionable insights.",
      category: "Business Intelligence",
    },
    {
      name: "Custom Software Development",
      description:
        "Enterprise-grade custom software solutions tailored to your specific business processes and operational workflows.",
      category: "Software Development",
    },
  ];

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: services.map((service, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "Service",
        serviceType: service.name,
        description: service.description,
        category: service.category,
        provider: {
          "@type": "Organization",
          name: "AI Genius Automations",
        },
      },
    })),
  };
};

/**
 * FAQ Schema - Use on FAQ page
 * Enables rich FAQ snippets in search results
 */
export const generateFAQSchema = (faqs: FAQItem[]) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
};

/**
 * BreadcrumbList Schema - Use on all interior pages
 * Improves navigation understanding and breadcrumb display in search
 */
export const generateBreadcrumbSchema = (breadcrumbs: BreadcrumbItem[]) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: crumb.url,
    })),
  };
};

/**
 * WebSite Schema with SearchAction - Use on homepage
 * Enables site search box in Google search results
 *
 * TODO: Implement actual search functionality before enabling this
 */
export const generateWebSiteSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    url: "https://aigeniusautomations.com",
    name: "AI Genius Automations",
    description: "Custom AI-powered automation solutions for businesses",
    // TODO: Implement search functionality first
    // "potentialAction": {
    //   "@type": "SearchAction",
    //   "target": "https://aigeniusautomations.com/search?q={search_term_string}",
    //   "query-input": "required name=search_term_string"
    // }
  };
};

/**
 * Article Schema - Use for blog posts (future use)
 * Enables rich article snippets
 */
export const generateArticleSchema = (article: {
  title: string;
  description: string;
  author: string;
  datePublished: string;
  dateModified?: string;
  image?: string;
}) => {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.description,
    author: {
      "@type": "Person",
      name: article.author,
    },
    publisher: {
      "@type": "Organization",
      name: "AI Genius Automations",
      logo: {
        "@type": "ImageObject",
        url: "https://aigeniusautomations.com/aig_consulting_logos_v4_darkLogoFull.png",
      },
    },
    datePublished: article.datePublished,
    dateModified: article.dateModified || article.datePublished,
    image:
      article.image || "https://aigeniusautomations.com/og-image-default.png",
  };
};

/**
 * Helper function to safely stringify JSON-LD
 * Handles undefined values and ensures valid JSON
 */
export const safeJSONLD = (schema: object): string => {
  return JSON.stringify(schema, (_key, value) =>
    value === undefined ? null : value,
  );
};
